import React, { useState } from 'react';
import { Box, MenuItem, ListItemIcon, ListItemText, Menu } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { MenuItemData } from 'framework/ui/menu';
import { DashboardMenuOption } from 'framework/ui/dashboard';
interface OptionsSectionProps<T> {
  options?: DashboardMenuOption[];
  data: T;
  onAction?: (action: string, data: T) => void;
  menuItemHeight?: string;
  fontSize?: string;
}

const OptionsSection = <T extends MenuItemData = MenuItemData>({
  options,
  data,
  onAction,
  menuItemHeight = '32px',
  fontSize = '12px',
}: OptionsSectionProps<T>) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  if (!options || options?.length < 1) return null;

  const handleClose = () => {
    setActiveMenu(null);
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option: T, e: React.MouseEvent<HTMLElement>) => {
    if (option.subOptions?.length) {
      setActiveMenu(option.key);
      setAnchorEl(e.currentTarget);
      return;
    }

    option.onClick?.(data);
    onAction?.(option.key, data);
    handleClose();
  };

  return (
    <Box
      sx={{
        mt: '-10px !important',
      }}
    >
      {options.map((option: DashboardMenuOption, index: number) => (
        <React.Fragment key={option.key}>
          <MenuItem
            onClick={(e: React.MouseEvent<HTMLElement>) => handleMenuItemClick(option as unknown as T, e)}
            disabled={option.disabled}
            sx={{
              color: 'var(--mui-palette-text-secondary)',
              width: '100%',
              '.MuiListItemText-primary': {
                fontWeight: 600,
              },
              paddingTop: '12px',
              paddingBottom: '12px',
            }}
          >
            {option.icon && (
              <ListItemIcon sx={{ minWidth: 'auto !important', marginRight: 1 }}>{option.icon}</ListItemIcon>
            )}
            <ListItemText primary={option.label} />
            {option.subOptions && <ArrowDropDownIcon fontSize="small" sx={{ marginTop: '2px' }} />}
          </MenuItem>
          {option.subOptions && activeMenu === option.key && (
            <>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ '& .MuiPaper-root': { minWidth: 148 } }}
              >
                {option.subOptions.map(subOption => (
                  <MenuItem
                    key={subOption.key}
                    onClick={() => {
                      subOption.onClick?.(data);
                      onAction?.(subOption.key, data);
                      handleClose();
                    }}
                    disabled={subOption.disabled}
                    sx={{
                      fontSize,
                      minHeight: menuItemHeight,
                      color: 'var(--mui-palette-text-secondary)',
                      '.MuiListItemText-primary': {
                        fontWeight: 600,
                      },
                    }}
                  >
                    {subOption.icon && <ListItemIcon sx={{ minWidth: '24px' }}>{subOption.icon}</ListItemIcon>}
                    <ListItemText primary={subOption.label} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default OptionsSection;
